import { useCallback, useEffect, useState, useRef } from 'react';
import { tripTypeValues } from '../../hooks/useFlightSearchCriteria';
import useExecutionQueue from './searchResults/useExecutionQueue';
import moment from '@g4/moment-utc';
import { CALENDAR_VIEW, LIST_VIEW, RETURNING } from './constants';

const useRoundTrip = (
  tripType,
  tabDates,
  returningQuery,
  departingQuery,
  makeRoundTrip,
  makeOneway,
  setReturningFlightOption,
  handleViewToggle,
  view,
  displayReturningSelectedDate,
  setDisplayReturningSelectedDate
) => {
  const [lateRoundTrip, setLateRoundTrip] = useState(false);
  const [isCreatingRoundTrip, setIsCreatingRoundTrip] = useState(false);

  const isRoundTrip = tripType === tripTypeValues.ROUNDTRIP;

  const scrollToRef = useRef();

  useEffect(() => {
    if (
      isRoundTrip &&
      isCreatingRoundTrip &&
      scrollToRef &&
      scrollToRef.current
    ) {
      window.scroll({ top: scrollToRef.current.offsetTop, behavior: 'smooth' });
    }
  }, [isCreatingRoundTrip, isRoundTrip, scrollToRef]);

  //set calendar view selected style (when adding/hiding a returning flight to one way)
  useEffect(() => {
    if (
      isRoundTrip &&
      view[RETURNING] === LIST_VIEW &&
      !displayReturningSelectedDate
    ) {
      setDisplayReturningSelectedDate(true);
    }
  }, [
    isRoundTrip,
    view,
    displayReturningSelectedDate,
    setDisplayReturningSelectedDate,
  ]);

  // Callbacks for the Round Trip banner functionality
  const onRoundTripBannerClick = useCallback(() => {
    makeRoundTrip();
    setLateRoundTrip(true);
    setIsCreatingRoundTrip(true);
    handleViewToggle(CALENDAR_VIEW, RETURNING);
    setDisplayReturningSelectedDate(false);
  }, [
    makeRoundTrip,
    setLateRoundTrip,
    setIsCreatingRoundTrip,
    handleViewToggle,
    setDisplayReturningSelectedDate,
  ]);
  const onCancelRounTripClick = useCallback(() => {
    makeOneway();
    setReturningFlightOption(null);
    setLateRoundTrip(false);
  }, [makeOneway, setLateRoundTrip, setReturningFlightOption]);

  // Delay changing from ROUNDTRIP to ONEWAY and the other way around
  // until the departing query is done loading to avoid an
  // Apollo bug: https://github.com/apollographql/apollo-client/issues/4114
  const [queueMakingRoundtrip, queueCancelingRoundtrip] = useExecutionQueue(
    [onRoundTripBannerClick, onCancelRounTripClick],
    !departingQuery.isFetching && !returningQuery.isFetching
  );

  // when the user creates a round-trip and flights have finished loading,
  // remove the flag which sets the loading indicator for return flights
  // and set the selected flight to the cheapest flight for the selected day
  useEffect(() => {
    if (
      !isCreatingRoundTrip ||
      (departingQuery.isFetching && returningQuery.isFetching)
    ) {
      return;
    }
    setIsCreatingRoundTrip(false);
    if (!returningQuery.flights) {
      return;
    }
    let selectedReturningFlight = null;
    returningQuery.flights.forEach(flight => {
      if (
        moment(flight.flight.departingTime, 'YYYY-MM-DD').isSame(
          moment(tabDates[RETURNING].selected, 'YYYY-MM-DD')
        )
      ) {
        if (
          !selectedReturningFlight ||
          flight.price < selectedReturningFlight.price
        ) {
          selectedReturningFlight = flight;
        }
      }
    });
    setReturningFlightOption(selectedReturningFlight);
  }, [
    isCreatingRoundTrip,
    setIsCreatingRoundTrip,
    returningQuery,
    departingQuery.isFetching,
    setReturningFlightOption,
    tabDates,
  ]);
  return {
    isRoundTrip,
    isCreatingRoundTrip,
    lateRoundTrip,
    scrollToRef,
    queueMakingRoundtrip,
    queueCancelingRoundtrip,
  };
};

export default useRoundTrip;
